/* global document */
import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_URL } from 'config.jsx';
import { UserInfoError, AuthenticationError } from 'layout/ErrorMessage';
import collectEvents from './collectEvents';

const withElephant = (Component) => {
  const elephantDataToFetch = [
    {
      fieldName: null, // save to root of the elephant object
      path: '/',
    },
    {
      fieldName: 'events',
      path: '/events/',
    },
    {
      fieldName: 'pedigrees',
      path: '/pedigrees/',
    },
    {
      fieldName: 'measureCodes',
      path: '/measurecodes/',
    },
  ];

  const hoc = class extends React.Component {
    constructor(props) {
      super(props);

      this.updateToElephant = this.updateToElephant.bind(this);
      this.fetchElephant = this.fetchElephant.bind(this);

      this.state = { elephant: null, networkError: null, loading: true };
    }

    componentDidMount() {
      const { match } = this.props;
      this.updateToElephant(match.params.id);
    }

    componentDidUpdate(prevProps) {
      const { match } = this.props;
      if (prevProps.match.params.id !== match.params.id) {
        this.updateToElephant(match.params.id);
      }
    }

    async fetchElephant(elephantId) {
      const fetchObjects = elephantDataToFetch.map(obj => Object.assign({}, obj));
      fetchObjects.forEach((obj) => {
        // add request promises to fetch objects
        obj.request = axios.get(`${API_URL}elephants/${elephantId}${obj.path}`);
      });

      try {
        // Wait for all requests to finish
        (await Promise.all(fetchObjects.map(obj => obj.request))).forEach(
          (response, index) => {
            // add responsesData objects to fetch objects
            fetchObjects[index].responseData = response.data;
          },
        );

        // create elephantData object and fill it with data from responses
        let elephantData = {};
        fetchObjects.forEach((obj) => {
          if (obj.fieldName) elephantData[obj.fieldName] = obj.responseData;
          else elephantData = Object.assign(elephantData, obj.responseData);
        });

        return elephantData;
      } catch (err) {
        if (err && err.response) {
          const { status } = err.response;
          if (status === 403) throw new AuthenticationError();
          if (status === 404) throw new UserInfoError('Elephant not found');
        }
        throw new Error('Network Error');
      }
    }

    splitAndSortMeasureCodesByLabel(measureCodes) {
      if (!measureCodes) return [];
      const newMeasureCodes = measureCodes.map(measureCode => (
        {
          ...measureCode,
          label: {
            orderNumber: measureCode.label.split(' ')[0],
            name: measureCode.label.substring(measureCode.label.indexOf(' ') + 1),
          },
        }));
      return newMeasureCodes.sort((a, b) => {
        if (a.label.orderNumber < b.label.orderNumber) return -1;
        if (a.label.orderNumber > b.label.orderNumber) return 1;
        return 0;
      });
    }

    async updateToElephant(elephantId) {
      this.setState({ loading: true, elephant: null, networkError: null });
      try {
        const elephant = await this.fetchElephant(elephantId);
        elephant.events = collectEvents(elephant);
        elephant.measureCodes = this.splitAndSortMeasureCodesByLabel(elephant.measureCodes);
        this.setState({ elephant, networkError: null });
        document.title = `${elephant.num || 'UKN'} - ${elephant.name || 'UKN'}`;
      } catch (err) {
        this.setState({ elephant: null, networkError: err });
      } finally {
        this.setState({ loading: false });
      }
    }

    render() {
      const { elephant, networkError, loading } = this.state;
      return (
        <Component
          {...this.props}
          elephant={elephant}
          networkError={networkError}
          loading={loading}
          withElephant
        />
      );
    }
  };
  hoc.propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  };
  return hoc;
};
export default withElephant;



// WEBPACK FOOTER //
// ./src/layout/withElephant/withElephant.jsx